// @ts-strict-ignore
/**
 * We have better implementation for our solution in terms of handling the modes of our `CollapsibleBars`.
 * Our new solution is a state machine. State machine is better if the transition is determined by the specific state based on its event.
 * This would avoid the unintentional transition to a state and more deterministic state transitioning.
 *
 * In our `CollapsibleBars`, we have 3 modes - `expand` | `collapse` | `visible``.
 * 'expand' mode - when the width of the `CollapsibleBar` is expanded and when the main content margin is given. Let say, main content will have margin value equal to width.
 * 'collapse' mode - when the width of the `CollapsibleBar` is collapsed. This will lead to a more area to the main content (Expanded).
 * 'visible' mode - when the width of the `CollapsibleBar` is expanded. The difference between `expand` and `visible`
 * is that if the mode is `visible`, the main content area is still expanded.
 *
 * The flow of our state machine. Note that `arrow` designates the direction of the state transition.
 *
 * Transition happens on `CLICK`.
 * `expand` => `collapse`
 * `collapse` => `expand`
 * `visible` => `expand`
 *
 * Transition happens on `MOUSE_ENTER`.
 * `collapse` => `visible`
 *
 * Transition happens on `MOUSE_LEAVE`
 * `visible` => `collapse`
 *
 * In state machine, it needs an event to start transitioning. These `CLICK`, `MOUSE_ENTER` and `MOUSE_LEAVE` will be the events which we gonna `dispatch` to create transition.
 *
 * */

export type CollapsedBarMode = 'expand' | 'collapse' | 'visible';
export type ModeEvents = 'CLICK' | 'MOUSE_ENTER' | 'MOUSE_LEAVE';

// The top level properties are the states. And its properties are all events where it listening to.
// The value of these `events` are the next transitioned state.
const stateMachineConfig: {
    [mode in CollapsedBarMode]: {
        [modeEvent in ModeEvents]?: CollapsedBarMode;
    };
} = {
    expand: {
        CLICK: 'collapse',
    },
    collapse: {
        CLICK: 'expand',
        MOUSE_ENTER: 'visible',
    },
    visible: {
        CLICK: 'expand',
        MOUSE_LEAVE: 'collapse',
    },
};

/**
 * Reducer is great util for state machine because it has an access to the prev state.
 * */
export const modeReducer = (current: CollapsedBarMode, event: ModeEvents) => {
    try {
        const val = stateMachineConfig[current];
        const nextState = val[event];
        if (nextState) {
            // Trigger a resize after the animation is done. Animation is set to
            // 300ms (see createSmoothTransitionStyles) so 500ms should be enough
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 500);
            return nextState;
        }
        return current;
    } catch (e) {
        const err = new Error(
            'Processing the <CollapsibleBar /> state transition triggers an error. Check the provided `state` and `event`.',
        );
        // Passing the error stack in here is great so that the error stack
        // will show where the error is trigger not on this catch block.
        err.stack = e.stack;
        throw err;
    }
};
